import React, { useState } from 'react';
import { IconExternalLink, IconBrandTwitter } from '@tabler/icons-react';
import { Button } from '../../../ui/button';
import { useTheme } from '../../../../contexts/theme/ThemeContext';

/**
 * Simple Twitter Iframe Component
 * Displays tweet using Twitter's embed iframe with instant loading
 */
const TwitterIframe = ({ tweetUrl, campaignName, onLoad }) => {
  const { isDarkMode } = useTheme();
  const [error, setError] = useState(false);
  const iframeRef = React.useRef(null);

  // Twitter brand color
  const twitterBlue = '#1DA1F2';

  // Handle iframe loading
  const handleIframeLoad = () => {
    setError(false);
    if (onLoad) onLoad();
  };

  const handleIframeError = () => {
    setError(true);
  };

  if (!tweetUrl) {
    return (
      <div className="p-6 text-center bg-surface-1 border-2 border-dashed border-glass-border">
        <IconBrandTwitter size={48} style={{ color: `${twitterBlue}80` }} className="mx-auto" />
        <p className="text-sm text-text-secondary mt-2">
          No tweet URL available
        </p>
      </div>
    );
  }

  // Extract tweet ID from URL
  const getTweetId = (url) => {
    const match = url.match(/status\/(\d+)/);
    return match ? match[1] : null;
  };

  const tweetId = getTweetId(tweetUrl);

  // Simple embed URL with theme
  const embedUrl = tweetId
    ? `https://platform.twitter.com/embed/Tweet.html?id=${tweetId}&theme=${isDarkMode ? 'dark' : 'light'}&width=500`
    : null;

  if (!embedUrl) {
    return (
      <div className="p-6 text-center bg-surface-1 border border-glass-border">
        <p className="text-sm text-text-secondary mb-4">
          Invalid tweet URL format
        </p>
        <Button
          variant="outline"
          size="sm"
          asChild
        >
          <a href={tweetUrl} target="_blank" rel="noopener noreferrer">
            <IconExternalLink size={16} className="mr-1" />
            View on Twitter
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* Twitter iframe - loads immediately */}
      <iframe
        ref={iframeRef}
        src={embedUrl}
        width="100%"
        height="500"
        frameBorder="0"
        scrolling="no"
        allowTransparency="true"
        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        onLoad={handleIframeLoad}
        onError={handleIframeError}
        style={{
          border: 'none',
          width: '100%',
          display: 'block',
          minHeight: '400px'
        }}
        title={`Tweet for ${campaignName}`}
      />

      {/* Simple fallback link */}
      <div className="mt-4 text-center">
        <a
          href={tweetUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm hover:underline"
          style={{ color: twitterBlue }}
        >
          <IconBrandTwitter size={16} />
          Open in Twitter
          <IconExternalLink size={14} />
        </a>
      </div>

      {/* Error state */}
      {error && (
        <div className="absolute top-2 right-2 bg-gray-400/90 text-white px-2 py-1 text-xs">
          Failed to load
        </div>
      )}
    </div>
  );
};

export default TwitterIframe;
